import type { Channel, ChannelListResponse, SubscriptionSummary } from '../../../api';
import type { ApiQueryResult } from '../../../lib/api/use-api-query';

import type { ScreenFailure } from './screen-failure';

/** What a channel list renders: exactly one of loading, a load failure, or the list (S5 §4, S9 §4). */
export type ChannelsView =
  | { readonly kind: 'loading' }
  | { readonly kind: 'failed'; readonly failure: ScreenFailure }
  | {
      readonly kind: 'list';
      readonly channels: readonly Channel[];
      readonly affiliateChannelUrl: string | null;
    };

/**
 * The view of `GET /channels` with the answers of the writes made since it arrived
 * (`useChannelWrites().overlay`): a summary, or `null` once removed. Extracted from the E3 route
 * so E12 builds its view the same way (S9 R20). A refresh failure while a list is on screen keeps
 * the list (`useApiQuery`), as before.
 */
export function toChannelsView(
  query: ApiQueryResult<ChannelListResponse>,
  overlay: ReadonlyMap<string, SubscriptionSummary | null>,
): ChannelsView {
  if (query.status === 'pending') return { kind: 'loading' };
  if (query.status === 'error') {
    return {
      kind: 'failed',
      failure: { reasonCode: query.error.reasonCode, params: query.error.params },
    };
  }
  return {
    kind: 'list',
    channels: query.data.channels.map((channel): Channel => {
      const answer = overlay.get(channel.id);
      return answer === undefined ? channel : { ...channel, subscription: answer };
    }),
    affiliateChannelUrl: query.data.affiliateChannelUrl,
  };
}
